import React, { useEffect, useMemo, useState } from 'react'

import {
    Button,
    Divider,
    useTheme,
    Tooltip,
    Checkbox,
    FormControlLabel,
    Grid,
    Switch,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import CancelPresentationIcon from '@material-ui/icons/CancelPresentation'
import PersonAddDisabledIcon from '@material-ui/icons/PersonAddDisabled'
import EventBusyIcon from '@material-ui/icons/EventBusy'
import InfoIcon from '@material-ui/icons/Info'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'

import {
    Container,
    DetailedToolTip,
    MultiSelector,
    NumberInput,
    Selector,
    SideDrawer,
} from '../../../components'
import {
    convertMapToList,
    ListVendor,
    ModelMap,
    BaseWorkorder,
    ServiceContractMap,
    BULK_TRANSITION,
    AreaConfig,
    IdBoolMap,
    convertListToMap,
    Service,
    PermissionRole,
    ASSIGN_WORKORDER_WALKER,
} from '../../../models'
import { fromPickerDate, isAccountManager, toMMDDYYYY } from '../../../helpers'
import { useTimezone } from '../../../contexts/timezone'
import {
    FinderSelection,
    useAnimationQueue,
    useAppDispatch,
    useSelectVendorService,
    useUser,
} from '../../../hooks'

import { RemoveListItem } from '../../../animations'
import { toast } from 'react-toastify'
import {
    BulkUpdateWorkOrderRequest,
    UnitRes,
    WorkorderAreaDict,
    bulkDeleteWorkorderRequest,
    bulkUpdateWorkorders,
} from '../../../store'
import { BulkTransitionEditor } from './BulkTransitionEditor'
import { hasPermission } from '../../../models/Users/services'
import { AreaSelectorCollection } from '../../../components/AreaSelector'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    open: boolean
    selectedWorkorders: ModelMap<BaseWorkorder>
    workordersInFilter: BaseWorkorder[]
    vendorList: ListVendor[]
    serviceList: Service[]
    removeWorkorder: (id: number) => void
    handleClose: () => void
    setSelectedWorkorders: (selectedWorkorders: ModelMap<BaseWorkorder>) => void
    upStreamContractMap: ServiceContractMap
    areaConfigMap: ModelMap<AreaConfig>
    unitList: UnitRes[]
    areaSelection: IdBoolMap
    setAreaSelection: (map: IdBoolMap) => void
    permissionRoles: PermissionRole[]
}

const ROW_HEIGHT = 100
const ROW_MARGIN = 1

const NEGATIVE_CHANGE = 0
const POSITIVE_CHANGE = 1
const NO_CHANGE = 2

type ToggleSelection =
    | typeof NEGATIVE_CHANGE
    | typeof NO_CHANGE
    | typeof POSITIVE_CHANGE

const _BulkEditSelectionList = (props: Props) => {
    const {
        open,
        selectedWorkorders,
        removeWorkorder,
        workordersInFilter,
        setSelectedWorkorders,
        vendorList,
        upStreamContractMap,
        handleClose,
        areaConfigMap,
        unitList,
        areaSelection,
        setAreaSelection,
        serviceList,
        permissionRoles,
    } = props

    const { workspaceUser } = useUser()
    const { currentTimezone } = useTimezone()

    const canEditApprovalRoles = hasPermission(
        workspaceUser,
        ASSIGN_WORKORDER_WALKER,
    )

    const theme = useTheme()
    const dispatch = useAppDispatch()

    const [dataFormOpen, setDataFormOpen] = useState(false)

    const [startDate, setStartDate] = useState<Date | null>(null)
    const [endDate, setEndDate] = useState<Date | null>(null)
    const [priority, setPriority] = useState<ToggleSelection>(NO_CHANGE)
    const [
        bedStatusSubscription,
        setBedStatusSubscription,
    ] = useState<ToggleSelection>(NO_CHANGE)
    const [addPrice, setAddPrice] = useState<number | undefined>()
    const [customPrice, setCustomPrice] = useState<number | undefined>()
    const [transitionModalOpen, setTransitionModalOpen] = useState(false)
    const [dateShiftEnabled, setDateShiftEnabled] = useState(false)
    const [dateShiftDays, setDateShiftDays] = useState<number>(0)
    const [dateShiftDirection, setDateShiftDirection] = useState<
        'forward' | 'backward'
    >('forward')

    const {
        selectService,
        selectVendor,
        selectedServiceId,
        selectedVendorId,
    } = useSelectVendorService(vendorList)

    const [editAreas, setEditAreas] = useState(false)
    const [editApprovalRoles, setEditApprovalRoles] = useState(false)
    const [approvalRoleIds, setApprovalRoleIds] = useState<number[]>([])

    const workorders = open ? convertMapToList(selectedWorkorders) : []
    const { enqueueAnimation, currentAnimation } = useAnimationQueue()

    const serviceMap = convertListToMap(serviceList)
    const vendorMap = convertListToMap(vendorList)

    const unitMap = convertListToMap(unitList)
    const selectedUnits: UnitRes[] = []
    workorders.forEach((wo) => {
        const unit = unitMap[wo.unit_id ?? -1]
        if (unit) {
            selectedUnits.push(unit)
        }
    })

    useEffect(() => {
        setStartDate(null)
        setEndDate(null)
        setPriority(NO_CHANGE)
        setBedStatusSubscription(NO_CHANGE)
        setAddPrice(undefined)
        setCustomPrice(undefined)
        selectVendor(-1)
        selectService(-1)
        setDateShiftEnabled(false)
        setDateShiftDays(0)
        setDateShiftDirection('forward')
        setEditApprovalRoles(false)
        setApprovalRoleIds([])
    }, [open])

    const isIntermediate =
        workorders.length > 0 && workorders.length < workordersInFilter.length

    // let servicesMatch = true
    let allUnlocked = true
    const sId = workorders.length > 0 ? workorders[0]?.service_id : -1
    // Get the first services id
    for (let i = 0; i < workorders.length; i++) {
        if (workorders[i]?.service_id !== sId) {
            // We found a service that does not match the first one
            // The user cannot update the vendor
            // servicesMatch = false
        }
        if (workorders[i]?.price_locked) {
            allUnlocked = false
        }
    }

    const {
        singleVendorOffersAllWoServices,
        singleVendorOffersSingleService,
        allWoVendorsOfferService,
    } = useMemo(() => {
        let singleVendorOffersAllWoServices = true
        let singleVendorOffersSingleService = true
        let allWoVendorsOfferService = true
        // Changing vendor and not service
        if (selectedServiceId === -1 && selectedVendorId !== -1) {
            const vendor = vendorMap[selectedVendorId]
            workorders.forEach((wo) => {
                const serviceValid =
                    vendor?.services.find((s) => s.id === wo.service_id) !==
                    undefined
                if (!serviceValid) {
                    singleVendorOffersAllWoServices = false
                }
            })
        }

        // Changing service and not vendor
        if (selectedServiceId !== -1 && selectedVendorId === -1) {
            workorders.forEach((wo) => {
                if (wo.vendor_id) {
                    const vendor = vendorMap[wo.vendor_id]

                    const serviceValid =
                        vendor?.services.find(
                            (s) => s.id === selectedServiceId,
                        ) !== undefined

                    if (!serviceValid) {
                        allWoVendorsOfferService = false
                    }
                }
            })
        }

        // Changing both service and vendor
        if (selectedServiceId !== -1 && selectedVendorId !== -1) {
            const vendor = vendorMap[selectedVendorId]
            singleVendorOffersSingleService =
                vendor?.services.find((s) => s.id === selectedServiceId) !==
                undefined
        }

        return {
            singleVendorOffersAllWoServices,
            singleVendorOffersSingleService,
            allWoVendorsOfferService,
        }
    }, [selectedServiceId, selectedVendorId, workorders])

    const validServices = useMemo(() => {
        if (selectedVendorId !== -1) {
            return vendorMap[selectedVendorId]?.services ?? []
        }
        return serviceList
    }, [selectedServiceId, selectedVendorId, serviceList])

    const canUpdateVendor = allUnlocked

    useEffect(() => {
        if (!canUpdateVendor) {
            selectVendor(-1)
        }
    }, [canUpdateVendor])

    useEffect(() => {
        if (!allUnlocked) {
            setStartDate(null)
        }
    }, [allUnlocked])

    useEffect(() => {
        if (!open) {
            setDataFormOpen(false)
        }
    }, [open])

    // Disable date shifting when specific dates are selected
    useEffect(() => {
        if (startDate !== null || endDate !== null) {
            setDateShiftEnabled(false)
        }
    }, [startDate, endDate])

    const createBulkUpdateBody = () => {
        // Workorders span whole days in the property's timezone, matching how
        // the assign modal creates them.
        const startDateIso =
            startDate !== null
                ? fromPickerDate(startDate, currentTimezone)
                      .startOf('day')
                      .toUTC()
                      .toISO()
                : null
        const endDateIso =
            endDate !== null
                ? fromPickerDate(endDate, currentTimezone)
                      .endOf('day')
                      .toUTC()
                      .toISO()
                : null

        const request: BulkUpdateWorkOrderRequest = {
            body: {
                workorders: workorders.map((wo) => wo.id),
                priority:
                    priority !== NO_CHANGE ? Boolean(priority) : undefined,
                start_date: startDateIso ?? undefined,
                end_date: endDateIso ?? undefined,
                add_price: addPrice,
                custom_price: customPrice,
                vendor: selectedVendorId !== -1 ? selectedVendorId : undefined,
                subscribe_to_bed_status_change:
                    bedStatusSubscription !== NO_CHANGE
                        ? Boolean(bedStatusSubscription)
                        : undefined,
                service:
                    selectedServiceId !== -1 ? selectedServiceId : undefined,
                days_earlier:
                    dateShiftEnabled &&
                    dateShiftDays > 0 &&
                    dateShiftDirection === 'backward'
                        ? dateShiftDays
                        : undefined,
                days_later:
                    dateShiftEnabled &&
                    dateShiftDays > 0 &&
                    dateShiftDirection === 'forward'
                        ? dateShiftDays
                        : undefined,
            },
        }

        if (editAreas) {
            const workorderAreaDict: WorkorderAreaDict[] = []
            // Go through the workorders
            workorders.forEach((wo) => {
                const serviceAreas: number[] = []
                const unit = unitMap[wo.unit_id ?? -1]
                unit?.areas.forEach((area) => {
                    if (areaSelection[area.id] === true) {
                        serviceAreas.push(area.id)
                    }
                })
                workorderAreaDict.push({
                    workorder_id: wo.id,
                    service_areas: serviceAreas,
                })
            })
            request.body.workorder_areas = workorderAreaDict
        }

        if (editApprovalRoles && canEditApprovalRoles) {
            request.body.approval_role_ids = approvalRoleIds
        }

        return request
    }

    const readyToSave =
        (addPrice !== undefined ||
            customPrice !== undefined ||
            startDate !== null ||
            endDate !== null ||
            priority !== NO_CHANGE ||
            selectedVendorId !== -1 ||
            bedStatusSubscription !== NO_CHANGE ||
            editAreas ||
            (editApprovalRoles && canEditApprovalRoles) ||
            selectedServiceId !== -1 ||
            (dateShiftEnabled && dateShiftDays > 0)) &&
        singleVendorOffersSingleService &&
        allWoVendorsOfferService &&
        singleVendorOffersAllWoServices

    return (
        <>
            <SideDrawer
                open={open}
                handleClose={handleClose}
                title={
                    <DrawerTitle
                        title={`Select (${workorders.length})`}
                        allUnlocked={allUnlocked}
                        canUpdateVendor={canUpdateVendor}
                    />
                }
                headerRight={
                    <FormControlLabel
                        style={{ marginRight: theme.spacing(1) }}
                        labelPlacement="start"
                        control={
                            <Checkbox
                                disabled={workordersInFilter.length === 0}
                                checked={
                                    workordersInFilter.length > 0 &&
                                    workorders.length ===
                                        workordersInFilter.length
                                }
                                indeterminate={isIntermediate}
                                onChange={() => {
                                    if (workorders.length > 0) {
                                        setSelectedWorkorders({})
                                        setAreaSelection({})
                                    } else {
                                        setSelectedWorkorders(
                                            workordersInFilter.reduce(
                                                (map, wo) => {
                                                    return {
                                                        ...map,
                                                        [wo.id]: wo,
                                                    }
                                                },
                                                {},
                                            ),
                                        )
                                        const tempAreaSelection = {
                                            ...areaSelection,
                                        }
                                        workordersInFilter.forEach((wo) => {
                                            wo.service_area_list.forEach(
                                                (area) => {
                                                    tempAreaSelection[
                                                        area.area_id
                                                    ] = true
                                                },
                                            )
                                        })
                                        setAreaSelection(tempAreaSelection)
                                    }
                                }}
                            />
                        }
                        label={
                            workorders.length > 0 ? 'Remove All' : 'Select All'
                        }
                    />
                }
            >
                <Container
                    style={{
                        flex: 1,
                        flexDirection: 'column',
                        overflowX: 'hidden',
                    }}
                    scrollY
                >
                    {workorders.map((wo, idx) => {
                        return (
                            <WorkorderRow
                                key={`EDIT_WORKORDER_${wo.id}`}
                                workorder={wo}
                                animationClassName={
                                    currentAnimation?.effectMap[wo.id]
                                        ?.animationCssClass
                                }
                                animationStyle={{
                                    ...currentAnimation?.effectMap[wo.id]
                                        ?.animationStyles,
                                }}
                                removeWorkorder={(id) => {
                                    enqueueAnimation(
                                        RemoveListItem(
                                            250,
                                            wo,
                                            workorders.slice(idx + 1),
                                            ROW_HEIGHT +
                                                theme.spacing(ROW_MARGIN) * 2,
                                            () => {
                                                removeWorkorder(id)
                                            },
                                        ),
                                    )
                                }}
                            />
                        )
                    })}
                </Container>

                <Divider />
                <Container
                    style={{
                        padding: theme.spacing(2),
                        justifyContent: 'flex-end',
                    }}
                >
                    <Button
                        color="secondary"
                        disabled={
                            !allUnlocked && !isAccountManager(workspaceUser)
                        }
                        variant="outlined"
                        style={{ marginRight: theme.spacing(2) }}
                        onClick={() => {
                            if (
                                confirm(
                                    `Are you sure you want to delete ${workorders.length} workorders?\n\nThis cannot be reversed!`,
                                )
                            ) {
                                dispatch(
                                    bulkDeleteWorkorderRequest({
                                        body: {
                                            workorders: workorders.map(
                                                (wo) => wo.id,
                                            ),
                                        },
                                    }),
                                ).then(() => {
                                    toast.success(
                                        `${workorders.length} Deleted`,
                                    )
                                    props.handleClose()
                                })
                            }
                        }}
                    >
                        Delete
                    </Button>

                    <div style={{ flex: 1 }} />

                    <Button
                        color="default"
                        variant="outlined"
                        style={{ marginRight: theme.spacing(2) }}
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                    <Button
                        color="primary"
                        variant="outlined"
                        style={{ marginRight: theme.spacing(2) }}
                        onClick={() => {
                            setDataFormOpen(true)
                        }}
                        disabled={workorders.length === 0}
                    >
                        Next
                    </Button>
                </Container>
            </SideDrawer>

            <SideDrawer
                open={dataFormOpen}
                handleClose={() => setDataFormOpen(false)}
                title={
                    <DrawerTitle
                        title={`Select (${workorders.length})`}
                        allUnlocked={allUnlocked}
                        canUpdateVendor={canUpdateVendor}
                    />
                }
            >
                <Container
                    style={{
                        flexDirection: 'column',
                        flex: 1,
                        overflowY: 'hidden',
                    }}
                >
                    <Container
                        style={{
                            flex: 1,
                            padding: theme.spacing(1),
                            flexDirection: 'column',
                        }}
                        scrollY
                    >
                        <Container style={{ alignItems: 'center' }}>
                            <Selector
                                disabled={!canUpdateVendor}
                                getDisplayString={(service) => service.name}
                                label="Service"
                                data={validServices}
                                currentValue={selectedServiceId}
                                onChange={(
                                    event: React.ChangeEvent<{
                                        value: unknown
                                    }>,
                                ) => {
                                    selectService(event.target.value as number)
                                }}
                                customStyle={{
                                    formControl: {
                                        margin: theme.spacing(2, 0),
                                        flex: 1,
                                    },
                                }}
                            />
                            <DetailedToolTip
                                headline="Can only be edited when:"
                                customStyle={{
                                    container: { marginLeft: theme.spacing(2) },
                                }}
                                messages={[
                                    {
                                        message:
                                            '1. All work orders are Assigned',
                                        warning: !allUnlocked,
                                    },
                                    {
                                        message:
                                            '2. Selected Vendor offers service (If a vendor is selected)',
                                        warning: !singleVendorOffersSingleService,
                                    },
                                    {
                                        message:
                                            '3. All the assigned vendors for all selected workorders offer this service (If no vendor is selected)',
                                        warning: !allWoVendorsOfferService,
                                    },
                                ]}
                            >
                                <InfoIcon
                                    fontSize="large"
                                    color={
                                        canUpdateVendor &&
                                        singleVendorOffersSingleService &&
                                        allWoVendorsOfferService
                                            ? 'action'
                                            : 'error'
                                    }
                                />
                            </DetailedToolTip>
                        </Container>
                        <Container style={{ alignItems: 'center' }}>
                            <Selector
                                disabled={!canUpdateVendor}
                                getDisplayString={(vendor) => vendor.name}
                                label="Vendor"
                                data={vendorList.filter((v) => {
                                    return (
                                        v.services.find((s) => s.id === sId) !==
                                        undefined
                                    )
                                })}
                                currentValue={selectedVendorId}
                                onChange={(
                                    event: React.ChangeEvent<{
                                        value: unknown
                                    }>,
                                ) => {
                                    selectVendor(event.target.value as number)
                                }}
                                customStyle={{
                                    formControl: {
                                        margin: theme.spacing(2, 0),
                                        flex: 1,
                                    },
                                }}
                            />
                            <DetailedToolTip
                                headline="Can only be edited when:"
                                customStyle={{
                                    container: { marginLeft: theme.spacing(2) },
                                }}
                                messages={[
                                    {
                                        message:
                                            '1. All work orders are Assigned',
                                        warning: !allUnlocked,
                                    },
                                    {
                                        message:
                                            '2. Selected Vendor offers service (If a service is selected)',
                                        warning: !singleVendorOffersSingleService,
                                    },
                                    {
                                        message:
                                            '3. Vendor offers service for all selected workorders (If no service is selected)',
                                        warning: !singleVendorOffersAllWoServices,
                                    },
                                ]}
                            >
                                <InfoIcon
                                    fontSize="large"
                                    color={
                                        canUpdateVendor &&
                                        singleVendorOffersSingleService &&
                                        singleVendorOffersAllWoServices
                                            ? 'action'
                                            : 'error'
                                    }
                                />
                            </DetailedToolTip>
                        </Container>

                        <Container style={{ alignItems: 'center' }}>
                            <KeyboardDatePicker
                                disabled={!allUnlocked}
                                value={startDate}
                                onChange={(date) => {
                                    setStartDate(date)
                                }}
                                format="MM/dd/yyyy"
                                inputVariant="outlined"
                                label="Start Date"
                                style={{ margin: theme.spacing(2, 0) }}
                                fullWidth
                                clearable
                            />

                            <DetailedToolTip
                                headline="Can only be edited when:"
                                customStyle={{
                                    container: { marginLeft: theme.spacing(2) },
                                }}
                                messages={[
                                    {
                                        message:
                                            '1. All work orders are Assigned',
                                        warning: !allUnlocked,
                                    },
                                ]}
                            >
                                <InfoIcon
                                    fontSize="large"
                                    color={allUnlocked ? 'action' : 'error'}
                                />
                            </DetailedToolTip>
                        </Container>

                        <KeyboardDatePicker
                            value={endDate}
                            onChange={(date) => {
                                setEndDate(date)
                            }}
                            format="MM/dd/yyyy"
                            inputVariant="outlined"
                            label="End Date"
                            style={{ margin: theme.spacing(2, 0) }}
                            fullWidth
                            clearable
                        />

                        {/* Date Shift Section */}
                        <Container
                            style={{
                                alignItems: 'center',
                                marginBottom: theme.spacing(2),
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.subtitle1,
                                    ),
                                    fontWeight: 700,
                                    margin: theme.spacing(1, 0),
                                }}
                            >
                                Shift Dates
                            </span>
                            <Switch
                                checked={dateShiftEnabled}
                                color="primary"
                                disabled={
                                    startDate !== null || endDate !== null
                                }
                                onClick={() => {
                                    setDateShiftEnabled(!dateShiftEnabled)
                                    if (dateShiftEnabled) {
                                        setDateShiftDays(0)
                                        setDateShiftDirection('forward')
                                    }
                                }}
                            />
                            <DetailedToolTip
                                headline="Date Shifting"
                                customStyle={{
                                    container: { marginLeft: theme.spacing(1) },
                                }}
                                messages={[
                                    {
                                        message:
                                            'Move workorder dates forward or backward by a specified number of days',
                                        warning: false,
                                    },
                                    {
                                        message:
                                            'Can only be used when specific start/end dates are set',
                                        warning: false,
                                    },
                                ]}
                            >
                                <InfoIcon fontSize="small" color="action" />
                            </DetailedToolTip>
                        </Container>

                        {dateShiftEnabled && (
                            <Container
                                style={{
                                    alignItems: 'center',
                                    marginBottom: theme.spacing(2),
                                }}
                            >
                                <ToggleButtonGroup
                                    value={dateShiftDirection}
                                    onChange={(_, direction) => {
                                        if (direction !== null) {
                                            setDateShiftDirection(direction)
                                        }
                                    }}
                                    aria-label="Date Shift Direction"
                                    exclusive
                                    size="small"
                                    style={{ marginRight: theme.spacing(2) }}
                                >
                                    <ToggleButton
                                        value="backward"
                                        aria-label="move-earlier"
                                    >
                                        ← Earlier
                                    </ToggleButton>
                                    <ToggleButton
                                        value="forward"
                                        aria-label="move-later"
                                    >
                                        Later →
                                    </ToggleButton>
                                </ToggleButtonGroup>

                                <NumberInput
                                    variant="outlined"
                                    label="Days"
                                    value={dateShiftDays ?? ''}
                                    style={{ flex: 1 }}
                                    onChange={(e) => {
                                        if (e.target.value === '') {
                                            setDateShiftDays(0)
                                        } else {
                                            const value = Number(e.target.value)
                                            setDateShiftDays(Math.max(0, value))
                                        }
                                    }}
                                    inputProps={{ min: 0 }}
                                />
                            </Container>
                        )}

                        <NumberInput
                            prefix={'$'}
                            variant="outlined"
                            label="Add Price"
                            value={addPrice ?? ''}
                            style={{ margin: theme.spacing(2, 0) }}
                            onChange={(e) => {
                                if (e.target.value === '') {
                                    setAddPrice(undefined)
                                } else {
                                    setAddPrice(Number(e.target.value))
                                }
                            }}
                        />

                        <NumberInput
                            prefix={'$'}
                            variant="outlined"
                            label="Custom Price"
                            value={customPrice ?? ''}
                            style={{ margin: theme.spacing(2, 0) }}
                            onChange={(e) => {
                                if (e.target.value === '') {
                                    setCustomPrice(undefined)
                                } else {
                                    setCustomPrice(Number(e.target.value))
                                }
                            }}
                        />

                        <Container style={{ alignItems: 'center' }}>
                            <span
                                style={{
                                    ...typographyStyle(
                                        theme.typography.subtitle1,
                                    ),
                                    fontWeight: 700,
                                    margin: theme.spacing(1, 0),
                                }}
                            >
                                Edit Areas
                            </span>
                            <Switch
                                checked={editAreas}
                                color="primary"
                                onClick={() => {
                                    setEditAreas(!editAreas)
                                }}
                            />
                        </Container>
                        {editAreas && (
                            <AreaSelectorCollection
                                areaConfigMap={areaConfigMap}
                                areaSelectionMap={areaSelection}
                                setAreaSelectionMap={setAreaSelection}
                                units={selectedUnits}
                                maxHeight="500px"
                                ignoreScroll
                            />
                        )}

                        {canEditApprovalRoles && (
                            <>
                                <Container style={{ alignItems: 'center' }}>
                                    <span
                                        style={{
                                            ...typographyStyle(
                                                theme.typography.subtitle1,
                                            ),
                                            fontWeight: 700,
                                            margin: theme.spacing(1, 0),
                                        }}
                                    >
                                        Edit Approval Roles
                                    </span>
                                    <Switch
                                        checked={editApprovalRoles}
                                        color="primary"
                                        onClick={() => {
                                            setEditApprovalRoles(
                                                !editApprovalRoles,
                                            )
                                            if (editApprovalRoles) {
                                                setApprovalRoleIds([])
                                            }
                                        }}
                                    />
                                    <Tooltip
                                        title={
                                            <span style={{ fontSize: 12 }}>
                                                Only users with these permission
                                                roles can Approve the selected
                                                workorders. If left empty,
                                                anyone with approval permission
                                                can approve them.
                                            </span>
                                        }
                                    >
                                        <InfoOutlinedIcon
                                            fontSize="small"
                                            color="action"
                                            style={{
                                                marginLeft: theme.spacing(1),
                                            }}
                                        />
                                    </Tooltip>
                                </Container>
                                {editApprovalRoles && (
                                    <MultiSelector
                                        label="Roles that can approve these workorders"
                                        keyIdentifier="BULK_EDIT_APPROVAL_ROLES"
                                        options={permissionRoles}
                                        selectedOptions={approvalRoleIds}
                                        onChange={setApprovalRoleIds}
                                        style={{
                                            formControl: {
                                                margin: theme.spacing(1, 0),
                                                width: '100%',
                                            },
                                        }}
                                    />
                                )}
                            </>
                        )}

                        <span
                            style={{
                                ...typographyStyle(theme.typography.subtitle1),
                                fontWeight: 700,
                                margin: theme.spacing(1, 0),
                            }}
                        >
                            Edit Priority
                        </span>
                        <ToggleButtonGroup
                            value={priority}
                            onChange={(_, priority) => {
                                setPriority(priority)
                            }}
                            aria-label="Priority Select"
                            exclusive
                        >
                            <ToggleButton
                                value={NEGATIVE_CHANGE}
                                aria-label="not-priority"
                            >
                                None
                            </ToggleButton>
                            <ToggleButton
                                value={NO_CHANGE}
                                aria-label="no-change"
                            >
                                No Changes
                            </ToggleButton>
                            <ToggleButton
                                value={POSITIVE_CHANGE}
                                aria-label="priority"
                            >
                                All
                            </ToggleButton>
                        </ToggleButtonGroup>

                        <span
                            style={{
                                ...typographyStyle(theme.typography.subtitle1),
                                fontWeight: 700,
                                margin: theme.spacing(1, 0),
                            }}
                        >
                            Work Order Locking
                        </span>
                        <ToggleButtonGroup
                            value={bedStatusSubscription}
                            onChange={(_, subscription) => {
                                setBedStatusSubscription(subscription)
                            }}
                            aria-label="Work Order Locking Select"
                            exclusive
                        >
                            <ToggleButton
                                value={NEGATIVE_CHANGE}
                                aria-label="lock"
                            >
                                Lock
                            </ToggleButton>
                            <ToggleButton
                                value={NO_CHANGE}
                                aria-label="no-change"
                            >
                                No Changes
                            </ToggleButton>
                            <ToggleButton
                                value={POSITIVE_CHANGE}
                                aria-label="unlock"
                            >
                                Unlock
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {/* Add transition button */}
                        <Container
                            style={{
                                justifyContent: 'flex-start',
                                marginTop: theme.spacing(2),
                            }}
                        >
                            <Button
                                color="primary"
                                variant="outlined"
                                disabled={
                                    !hasPermission(
                                        workspaceUser,
                                        BULK_TRANSITION,
                                    )
                                }
                                onClick={() =>
                                    setTransitionModalOpen(!transitionModalOpen)
                                }
                            >
                                Transition
                            </Button>
                        </Container>

                        <BulkTransitionEditor
                            workorders={workorders}
                            isOpen={transitionModalOpen}
                            handleBulkTransitionClose={() =>
                                setTransitionModalOpen(false)
                            }
                            upStreamContractMap={upStreamContractMap}
                            setSelectedWorkorders={setSelectedWorkorders}
                            handleBulkEditClose={handleClose}
                            handleDataFormClose={() => setDataFormOpen(false)}
                        />
                    </Container>

                    <Divider />
                    <Container
                        style={{
                            padding: theme.spacing(2),
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            color="secondary"
                            variant="outlined"
                            style={{ marginRight: theme.spacing(2) }}
                            onClick={() => setDataFormOpen(false)}
                        >
                            Previous
                        </Button>

                        <Button
                            disabled={!readyToSave}
                            color="primary"
                            variant="outlined"
                            style={{ marginRight: theme.spacing(2) }}
                            onClick={() => {
                                let message = `You are bulk editing ${workorders.length} workorders \n\n`
                                if (addPrice !== undefined) {
                                    message += `-Add Price: $${addPrice}\n`
                                }
                                if (customPrice !== undefined) {
                                    message += `-Custom Price: $${customPrice}\n`
                                }
                                if (startDate !== null) {
                                    message += `-Start Date: ${toMMDDYYYY(
                                        startDate,
                                    )}\n`
                                }
                                if (endDate !== null) {
                                    message += `-End Date: ${toMMDDYYYY(
                                        endDate,
                                    )}\n`
                                }
                                if (dateShiftEnabled && dateShiftDays > 0) {
                                    if (
                                        startDate !== null ||
                                        endDate !== null
                                    ) {
                                        message += `-Date Shift: ${dateShiftDays} days ${
                                            dateShiftDirection === 'forward'
                                                ? 'later'
                                                : 'earlier'
                                        }\n`
                                        if (startDate) {
                                            message += `-Start Date: ${toMMDDYYYY(
                                                startDate,
                                            )}\n`
                                        }
                                        if (endDate) {
                                            message += `-End Date: ${toMMDDYYYY(
                                                endDate,
                                            )}\n`
                                        }
                                    } else {
                                        message += `-Shift existing dates: ${dateShiftDays} days ${
                                            dateShiftDirection === 'forward'
                                                ? 'later'
                                                : 'earlier'
                                        }\n`
                                    }
                                }

                                if (priority !== NO_CHANGE) {
                                    message += `-Priority: ${
                                        priority === NEGATIVE_CHANGE
                                            ? 'None'
                                            : 'All'
                                    }\n`
                                }

                                if (selectedVendorId !== -1) {
                                    message += `-Vendor: ${
                                        vendorList.find(
                                            (v) => v.id === selectedVendorId,
                                        )?.name
                                    }\n`
                                }

                                if (bedStatusSubscription !== NO_CHANGE) {
                                    message += `-Work Order Locking: ${
                                        bedStatusSubscription ===
                                        NEGATIVE_CHANGE
                                            ? 'Lock'
                                            : 'Unlock'
                                    }\n`
                                }

                                if (editAreas) {
                                    message +=
                                        '-Service Areas: Confirm the correct areas are selected'
                                }

                                if (editApprovalRoles && canEditApprovalRoles) {
                                    const roleNames = approvalRoleIds
                                        .map(
                                            (id) =>
                                                permissionRoles.find(
                                                    (r) => r.id === id,
                                                )?.name,
                                        )
                                        .filter(Boolean)
                                        .join(', ')
                                    message += `-Approval Roles: ${
                                        roleNames.length > 0
                                            ? roleNames
                                            : 'None (anyone with approval permission can approve)'
                                    }\n`
                                }

                                if (selectedServiceId !== -1) {
                                    message += `-Service: ${
                                        serviceMap[selectedServiceId]?.name ??
                                        ''
                                    }`
                                }

                                if (confirm(message)) {
                                    const request = createBulkUpdateBody()

                                    dispatch(
                                        bulkUpdateWorkorders(request),
                                    ).then(() => {
                                        toast.success(
                                            `${workorders.length} Workorders updated!`,
                                        )
                                        props.handleClose()
                                    })
                                }
                            }}
                        >
                            Next
                        </Button>
                    </Container>
                </Container>
            </SideDrawer>
        </>
    )
}

interface RowProps {
    workorder: BaseWorkorder
    animationClassName?: string
    animationStyle?: React.CSSProperties
    removeWorkorder: (id: number) => void
}

const WorkorderRow = (props: RowProps) => {
    const {
        workorder,
        removeWorkorder,
        animationClassName,
        animationStyle,
    } = props

    const theme = useTheme()

    return (
        <Container
            style={{
                minHeight: ROW_HEIGHT,
                maxHeight: ROW_HEIGHT,
                margin: theme.spacing(ROW_MARGIN),
                borderRadius: theme.shape.borderRadius,
                backgroundColor: theme.palette.grey[100],
                boxSizing: 'border-box',
                ...animationStyle,
            }}
            className={animationClassName}
        >
            {/* Col 1 */}
            <Container
                style={{
                    padding: theme.spacing(1),
                    flex: 1,
                    borderTopLeftRadius: theme.shape.borderRadius,
                    borderBottomLeftRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    borderRight: 0,
                }}
            >
                <Container style={{ flexDirection: 'column' }}>
                    {/* Row 1 */}
                    <Container style={{ alignItems: 'center' }}>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.subtitle1),
                                fontWeight: 700,
                            }}
                        >
                            {workorder.service_name ?? ''}
                        </span>

                        <span
                            style={{
                                ...typographyStyle(theme.typography.subtitle2),
                                marginLeft: theme.spacing(1),
                            }}
                        >
                            {/* {toMMDDYYYY(new Date(workorder.start_date))} */}
                        </span>
                    </Container>

                    {/* Row 2 */}
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle2),
                        }}
                    >
                        {workorder.vendor_name}
                    </span>

                    <div style={{ flex: 1 }} />

                    {/* Row 3 */}
                    <Container>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                fontWeight: 700,
                            }}
                        >
                            {workorder.path ?? ''}
                        </span>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.body2),
                                fontWeight: 700,
                            }}
                        >
                            {workorder.folder_name ?? ''}
                            {workorder.folder_name && '/'}
                        </span>
                        <span style={{ marginLeft: theme.spacing(1) }}>
                            {workorder.unit_name ?? ''}
                        </span>
                    </Container>
                </Container>
                {/* ICONS */}
                <Grid container alignContent="center">
                    <Grid
                        item
                        container
                        xs={6}
                        alignContent="center"
                        justify="center"
                    >
                        {workorder.price_locked ? (
                            <Tooltip title="Start Date Locked">
                                <EventBusyIcon
                                    fontSize="large"
                                    color="action"
                                />
                            </Tooltip>
                        ) : null}
                    </Grid>

                    <Grid
                        item
                        container
                        xs={6}
                        alignContent="center"
                        justify="center"
                    >
                        {workorder.price_locked ? (
                            <Tooltip title="Vendor Locked">
                                <PersonAddDisabledIcon
                                    fontSize="large"
                                    color="action"
                                />
                            </Tooltip>
                        ) : null}
                    </Grid>
                </Grid>
            </Container>
            {/* End Col 1 */}

            {/* End Col 2 */}

            {/* Col 3 Close Button*/}
            <Container
                style={{
                    padding: theme.spacing(1),
                    borderTopRightRadius: theme.shape.borderRadius,
                    borderBottomRightRadius: theme.shape.borderRadius,
                    backgroundColor: theme.palette.secondary.light,
                    alignItems: 'flex-end',
                    flexDirection: 'column',
                    boxShadow: `inset 2px 0px 5px ${theme.palette.secondary.dark}`,
                    cursor: 'pointer',
                }}
                onClick={() => removeWorkorder(workorder.id)}
            >
                <div style={{ flex: 1 }} />
                <div>
                    <Tooltip title="Remove from selection">
                        <CancelPresentationIcon fontSize="large" />
                    </Tooltip>
                </div>

                <div style={{ flex: 1 }} />
            </Container>
            {/* End Col 3 */}
        </Container>
    )
}

interface DrawerTitleProps {
    title: string
    allUnlocked: boolean
    canUpdateVendor: boolean
}

const DrawerTitle = (props: DrawerTitleProps) => {
    const { title, allUnlocked, canUpdateVendor } = props

    const theme = useTheme()

    return (
        <Container style={{ flexDirection: 'column' }}>
            <Container>
                <span
                    style={{
                        ...typographyStyle(theme.typography.h6),
                        fontWeight: 500,
                    }}
                >
                    {title}
                </span>
            </Container>

            <Container>
                {!allUnlocked ? (
                    <Tooltip title="Start Date Locked">
                        <EventBusyIcon fontSize="small" color="action" />
                    </Tooltip>
                ) : null}
                {!canUpdateVendor ? (
                    <Tooltip title="Vendor Locked">
                        <PersonAddDisabledIcon
                            fontSize="small"
                            color="action"
                        />
                    </Tooltip>
                ) : null}
            </Container>
        </Container>
    )
}

export const BulkEditSelectionList = React.memo(_BulkEditSelectionList)
